import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { usePermissionCheck } from '../../contexts/PermissionContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { getAllowedSecurityLevelsForRole, getHighestAllowedSecurityLevelForRole } from '../../config/documentSecurity'
import apiService from '../../services/api'
import apiConfig from '../../config/apiConfig'
import BlockedAction, { BlockedBanner } from '../common/BlockedAction'
import { useActionGate } from '../../contexts/BlockPolicyContext'
import {
  HiOutlineArrowUpTray,
  HiOutlineXMark,
  HiOutlineDocument,
  HiOutlineExclamationTriangle,
  HiOutlineCheckCircle,
  HiOutlineInformationCircle,
  HiOutlineShieldCheck,
  HiOutlineShieldExclamation,
  HiOutlineFolder,
  HiOutlineUser,
  HiOutlineScale,
  HiOutlineCalendar,
  HiOutlineChatBubbleLeft,
  HiOutlineSpeakerWave,
  HiOutlineMegaphone
} from 'react-icons/hi2'
import { Button, Modal } from '../ui'

const DocumentUploadModal = ({ isOpen, onClose, onUploadSuccess, preselectedClient, preselectedCase, preselectedSession, disablePreselectedFields = false, preselectedGroup = null }) => {
  const { t } = useTranslation()
  const { isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError, showWarning } = useAlerts()
  const { userRole } = usePermissionCheck()
  const { getEntity } = useWorkspace()

  // Ref to track when auto-populating from preselectedGroup (prevents cascading useEffects from clearing values)
  const isAutoPopulatingRef = useRef(false)

  // Get allowed security levels based on user role
  // Get allowed security levels for current user role using centralized config
  const role = userRole?.toLowerCase() || 'silver'
  const allowedSecurityLevels = getAllowedSecurityLevelsForRole(role)
  const defaultSecurityLevel = getHighestAllowedSecurityLevelForRole(role)
  
  const [files, setFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [dragOver, setDragOver] = useState(false)
  const [uploadMode, setUploadMode] = useState('single') // 'single', 'group'
  const [documentType, setDocumentType] = useState('document') // 'document', 'text', 'voice'

  // Resolve the right block-policy action key per document type so the gate
  // applied to the submit button matches what the user is currently doing.
  const uploadAction = documentType === 'voice'
    ? 'upload_voice'
    : documentType === 'text'
      ? 'upload_text'
      : 'upload_document'
  const uploadGate = useActionGate(uploadAction)

  const [messageScope, setMessageScope] = useState('direct') // 'direct', 'broadcast'
  const [messageContent, setMessageContent] = useState('')
  const [voiceFile, setVoiceFile] = useState(null)

  // Form data
  const [formData, setFormData] = useState({
    security_level: defaultSecurityLevel,
    client_id: '',
    case_id: '',
    session_id: '',
    group_id: '',
    title: '',
    create_new_group: false,
    new_group_name: '',
    new_group_description: '',
    join_to_group: false
  })

  // Options
  const [clients, setClients] = useState([])
  const [cases, setCases] = useState([])
  const [sessions, setSessions] = useState([])
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      fetchClients()
      fetchGroups()
    }
  }, [isOpen])

  // Reset form when modal opens - ensure complete reset of all state
  useEffect(() => {
    if (isOpen) {
      // Reset the auto-populating flag
      isAutoPopulatingRef.current = false
      // Always reset ALL form state first
      setFiles([])
      setUploadMode('single')
      setDocumentType('document')
      setMessageScope('direct')
      setMessageContent('')
      setVoiceFile(null)
      setUploading(false)
      setUploadProgress(0)
      setFormData({
        security_level: defaultSecurityLevel,
        client_id: '',
        case_id: '',
        session_id: '',
        group_id: '',
        title: '',
        create_new_group: false,
        new_group_name: '',
        new_group_description: '',
        join_to_group: false
      })
      setCases([])
      setSessions([])
    }
  }, [isOpen, defaultSecurityLevel])

  useEffect(() => {
    if (formData.client_id) {
      fetchCases(formData.client_id)
    } else {
      setCases([])
      setSessions([])
    }
  }, [formData.client_id])

  useEffect(() => {
    if (formData.case_id) {
      fetchSessions(formData.case_id)
    } else {
      setSessions([])
    }
  }, [formData.case_id])

  // Fetch groups when client_id changes (for "upload to existing group" mode)
  useEffect(() => {
    if (formData.client_id) {
      fetchGroups()
      // Clear selected group when client changes - but NOT if we're auto-populating from preselectedGroup
      if (!isAutoPopulatingRef.current) {
        setFormData(prev => ({ ...prev, group_id: '' }))
      }
    } else {
      setGroups([])
      setFormData(prev => ({ ...prev, group_id: '' }))
    }
  }, [formData.client_id])

  // Refetch groups when session changes to further filter
  useEffect(() => {
    if (formData.session_id && formData.client_id) {
      fetchGroups()
      // Clear selected group when session changes - but NOT if we're auto-populating from preselectedGroup
      if (!isAutoPopulatingRef.current) {
        setFormData(prev => ({ ...prev, group_id: '' }))
      }
    }
  }, [formData.session_id])

  // Auto-populate form with preselected values (runs after form reset)
  useEffect(() => {
    if (isOpen && (preselectedClient || preselectedCase || preselectedSession || preselectedGroup)) {
      console.log('Auto-populating form with preselected values:', { preselectedClient, preselectedCase, preselectedSession, preselectedGroup })

      // Use setTimeout to ensure this runs after the form reset
      setTimeout(() => {
        const updates = {}

        if (preselectedGroup) {
          // Set flag to prevent cascading useEffects from clearing group_id
          isAutoPopulatingRef.current = true

          // When preselecting a group, auto-populate related fields and set group mode
          if (preselectedGroup.clientId) updates.client_id = String(preselectedGroup.clientId)
          if (preselectedGroup.caseId) updates.case_id = String(preselectedGroup.caseId)
          if (preselectedGroup.sessionId) updates.session_id = String(preselectedGroup.sessionId)
          if (preselectedGroup.groupId) updates.group_id = String(preselectedGroup.groupId)
          updates.create_new_group = false // Use existing group
          setUploadMode('group') // Force group mode when preselecting a group
        } else {
          // Regular preselection logic
          if (preselectedClient && preselectedClient !== '') {
            updates.client_id = String(preselectedClient)
          }
          if (preselectedCase && preselectedCase !== '') {
            updates.case_id = String(preselectedCase)
          }
          if (preselectedSession && preselectedSession !== '') {
            updates.session_id = String(preselectedSession)
          }
        }

        if (Object.keys(updates).length > 0) {
          console.log('Applying updates to form:', updates)
          setFormData(prev => ({
            ...prev,
            ...updates
          }))

          // Reset the flag after a short delay to allow the state updates to complete
          if (preselectedGroup) {
            setTimeout(() => {
              isAutoPopulatingRef.current = false
            }, 200)
          }
        }
      }, 100) // Small delay to ensure form reset happens first
    }
  }, [isOpen, preselectedClient, preselectedCase, preselectedSession, preselectedGroup])

  const fetchClients = async () => {
    try {
      const params = isWorkspaceSwitched && ownerAccountId 
        ? { owner_account_id: ownerAccountId }
        : {}

      const response = await apiService.get('/clients/', { params })
      if (response.success) {
        const responseData = response.data.data || response.data
        const clientsArray = responseData.data || responseData.results || []
        setClients(clientsArray)
      }
    } catch (error) {
      console.error('Error fetching clients:', error)
    }
  }

  const fetchCases = async (clientId) => {
    try {
      const params = {
        client_id: clientId,
        ...(isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {})
      }

      const response = await apiService.get('/cases/', { params })
      if (response.success) {
        const responseData = response.data.data || response.data
        const casesArray = responseData.data || responseData.results || []
        setCases(casesArray)
      }
    } catch (error) {
      console.error('Error fetching cases:', error)
    }
  }

  const fetchSessions = async (caseId) => {
    try {
      const params = {
        case_id: caseId,
        ...(isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {})
      }

      const response = await apiService.get('/sessions/', { params })
      if (response.success) {
        const responseData = response.data.data || response.data
        const sessionsArray = responseData.data || responseData.results || []
        setSessions(sessionsArray)
      }
    } catch (error) {
      console.error('Error fetching sessions:', error)
    }
  }

  const fetchGroups = async () => {
    try {
      const params = {
        ...(isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {})
      }

      // Filter by client_id when available (required for "upload to existing group" mode)
      if (formData.client_id) {
        params.client_id = formData.client_id
      }

      // Further filter by session_id if selected
      if (formData.session_id) {
        params.session_id = formData.session_id
      }

      const response = await apiService.get('/document-groups/', { params })
      if (response.success) {
        const responseData = response.data.data || response.data
        // Handle both array and object with nested data/results
        const groupsArray = Array.isArray(responseData)
          ? responseData
          : (responseData.data || responseData.results || [])
        setGroups(groupsArray)
      }
    } catch (error) {
      console.error('Error fetching groups:', error)
    }
  }

  const handleFileSelect = (selectedFiles, append = false) => {
    const fileArray = Array.from(selectedFiles)

    // Validate file sizes (25MB limit)
    const maxSize = apiConfig.MAX_FILE_SIZE
    const invalidFiles = fileArray.filter(file => file.size > maxSize)

    if (invalidFiles.length > 0) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(0)
      // TODO: needs i18n key
      showError(
        `${invalidFiles.length} file(s) exceed the ${maxSizeMB}MB size limit: ${invalidFiles.map(f => f.name).join(', ')}`
      )
      return
    }

    const validFiles = fileArray

    if (validFiles.length > 0) {
      // For single mode, allow multiple files (they'll get auto-numbered names)
      if (uploadMode === 'single') {
        if (append) {
          setFiles(prev => {
            const existingFileNames = prev.map(f => f.name)
            const newFiles = validFiles.filter(f => !existingFileNames.includes(f.name))
            if (newFiles.length < validFiles.length) {
              // TODO: needs i18n key
              showWarning('Some files were skipped because they were already selected')
            }
            return [...prev, ...newFiles]
          })
        } else {
          setFiles(validFiles)
        }
      } else {
        // For group mode, append or replace files
        if (append) {
          setFiles(prev => {
            const existingFileNames = prev.map(f => f.name)
            const newFiles = validFiles.filter(f => !existingFileNames.includes(f.name))
            if (newFiles.length < validFiles.length) {
              // TODO: needs i18n key
              showWarning('Some files were skipped because they were already selected')
            }
            return [...prev, ...newFiles]
          })
        } else {
          setFiles(validFiles)
        }

        // Don't auto-change mode - user should explicitly choose single or group
      }
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    const droppedFiles = e.dataTransfer.files
    handleFileSelect(droppedFiles)
  }

  const handleFileInputChange = (e) => {
    const selectedFiles = e.target.files
    handleFileSelect(selectedFiles)
  }

  const removeFile = (indexToRemove) => {
    setFiles(prev => {
      const newFiles = prev.filter((_, index) => index !== indexToRemove)

      // Don't auto-change upload mode when removing files

      return newFiles
    })
  }

  const addMoreFiles = () => {
    // Trigger file input click to add more files
    document.getElementById('add-more-files-input').click()
  }

  const handleAddMoreFiles = (e) => {
    const selectedFiles = e.target.files
    if (selectedFiles.length > 0) {
      handleFileSelect(selectedFiles, true) // append = true
    }
    // Clear the input so the same file can be selected again if needed
    e.target.value = ''
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const validateForm = () => {
    // --- Validation for file document uploads ---
    if (documentType === 'document') {
      if (files.length === 0) {
        // TODO: needs i18n key
        showError('Please select at least one file to upload')
        return false
      }
      if (!formData.client_id) {
        // TODO: needs i18n key
        showError(`Please select a ${getEntity('client').singular.toLowerCase()}`)
        return false
      }
      if (uploadMode === 'single') {
        if (!formData.title.trim()) {
          // TODO: needs i18n key
          showError(`Please enter a ${getEntity('document').singular.toLowerCase()} name`)
          return false
        }
        if (formData.title.trim().length < 8) {
          // TODO: needs i18n key
          showError(`${getEntity('document').singular} name must be at least 8 characters`)
          return false
        }
      }
      if (uploadMode === 'single' && formData.join_to_group && !formData.group_id) {
        // TODO: needs i18n key
        showError('Please select a group or uncheck "Join to Existing Group"')
        return false
      }
      if (uploadMode === 'group') {
        if (formData.create_new_group) {
          if (!formData.new_group_name.trim()) {
            // TODO: needs i18n key
            showError('Please enter a name for the new group')
            return false
          }
        } else if (!formData.group_id) {
          // TODO: needs i18n key
          showError('Please select an existing group or create a new one')
          return false
        }
      }
      return true
    }

    // --- Validation for text messages ---
    if (documentType === 'text') {
      if (!messageContent.trim()) {
        showError(t('messages.create.validationContentRequired'))
        return false
      }
      if (messageScope === 'direct' && !formData.client_id && !formData.case_id && !formData.session_id) {
        showError(t('messages.create.validationTargetRequired', {
          caseEntity: getEntity('case').singular.toLowerCase(),
          clientEntity: getEntity('client').singular.toLowerCase(),
          sessionEntity: getEntity('session').singular.toLowerCase(),
        }))
        return false
      }
      return true
    }

    // --- Validation for voice messages ---
    if (documentType === 'voice') {
      if (!voiceFile) {
        showError(t('messages.create.validationVoiceRequired'))
        return false
      }
      if (!voiceFile.type.startsWith('audio/')) {
        // TODO: needs i18n key
        showError('Only audio files are allowed')
        return false
      }
      if (voiceFile.size > 10 * 1024 * 1024) {
        // TODO: needs i18n key
        showError('Voice file cannot exceed 10MB')
        return false
      }
      if (messageScope === 'direct' && !formData.client_id && !formData.case_id && !formData.session_id) {
        showError(t('messages.create.validationTargetRequired', {
          caseEntity: getEntity('case').singular.toLowerCase(),
          clientEntity: getEntity('client').singular.toLowerCase(),
          sessionEntity: getEntity('session').singular.toLowerCase(),
        }))
        return false
      }
      return true
    }

    return true
  }

  const handleUpload = async () => {
    if (!validateForm()) return

    // Server-side enforcement is the source of truth, but bail out early
    // if the policy says we're blocked — saves a wasted upload roundtrip
    // and gives the user a clearer message than the 402 they'd otherwise see.
    if (uploadGate && uploadGate.allowed === false) {
      // TODO: needs i18n key
      showError(uploadGate.message || 'This action is currently blocked.')
      return
    }

    try {
      setUploading(true)
      setUploadProgress(0)

      const params = isWorkspaceSwitched && ownerAccountId
        ? { owner_account_id: ownerAccountId }
        : {}

      let response

      // --- Handle text/voice message creation ---
      if (documentType === 'text' || documentType === 'voice') {
        const submitData = documentType === 'voice' ? new FormData() : null

        if (documentType === 'voice') {
          submitData.append('type', 'voice')
          submitData.append('voice_file', voiceFile)
          submitData.append('security_level', formData.security_level)
          submitData.append('message_scope', messageScope)
          if (formData.title.trim()) submitData.append('title', formData.title.trim())
          if (messageScope === 'direct') {
            if (formData.client_id) submitData.append('client_id', formData.client_id)
            if (formData.case_id) submitData.append('case_id', formData.case_id)
            if (formData.session_id) submitData.append('session_id', formData.session_id)
          }
          response = await apiService.post('/documents/create/', submitData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            params,
          })
        } else {
          const jsonData = {
            type: 'text',
            content: messageContent.trim(),
            security_level: formData.security_level,
            message_scope: messageScope,
            title: formData.title.trim() || undefined,
          }
          if (messageScope === 'direct') {
            if (formData.client_id) jsonData.client_id = parseInt(formData.client_id)
            if (formData.case_id) jsonData.case_id = parseInt(formData.case_id)
            if (formData.session_id) jsonData.session_id = parseInt(formData.session_id)
          }
          response = await apiService.post('/documents/create/', jsonData, { params })
        }

        if (response.success) {
          // TODO: needs i18n key (voice variant)
          showSuccess(documentType === 'text' ? t('messages.create.sendSuccess') : 'Voice message sent successfully')
          onUploadSuccess?.()
        } else {
          showError(response.error?.message || t('messages.create.sendFailed'))
        }
        return
      }

      // --- Handle file document upload (existing logic) ---
      const uploadFormData = new FormData()

      files.forEach((file) => {
        uploadFormData.append('files', file)
      })

      uploadFormData.append('security_level', formData.security_level)
      uploadFormData.append('client_id', formData.client_id)

      if (formData.case_id) uploadFormData.append('case_id', formData.case_id)
      if (formData.session_id) uploadFormData.append('session_id', formData.session_id)

      if (uploadMode === 'group') {
        uploadFormData.append('store_as_group', 'true')
        if (formData.create_new_group) {
          uploadFormData.append('group_name', formData.new_group_name.trim())
          if (formData.new_group_description.trim()) {
            uploadFormData.append('group_description', formData.new_group_description.trim())
          }
        } else if (formData.group_id) {
          uploadFormData.append('group_id', formData.group_id)
        }
      } else {
        uploadFormData.append('store_as_group', 'false')
      }

      if (uploadMode === 'single' && formData.title.trim()) {
        uploadFormData.append('title', formData.title.trim())
      }

      if (uploadMode === 'single' && formData.join_to_group && formData.group_id) {
        uploadFormData.append('join_to_group', 'true')
        uploadFormData.append('group_id', formData.group_id)
      }

      response = await apiService.post('/documents/upload/', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          setUploadProgress(percentCompleted)
        }
      })

      if (response.success) {
        const uploadedCount = files.length
        setUploadProgress(100)
        // TODO: needs i18n key
        showSuccess(`Successfully uploaded ${uploadedCount} ${uploadedCount !== 1 ? getEntity('document').plural.toLowerCase() : getEntity('document').singular.toLowerCase()}`)
        onUploadSuccess?.()
      } else {
        // TODO: needs i18n key
        showError(response.error?.message || 'Upload failed')
      }

    } catch (error) {
      console.error('Upload error:', error)
      // TODO: needs i18n key
      showError('Failed. Please try again.')
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const handleClose = () => {
    setFiles([])
    setFormData({
      security_level: defaultSecurityLevel,
      client_id: '',
      case_id: '',
      session_id: '',
      group_id: '',
      title: '',
      create_new_group: false,
      new_group_name: '',
      new_group_description: ''
    })
    setUploadMode('single')
    setDocumentType('document')
    setMessageScope('direct')
    setMessageContent('')
    setVoiceFile(null)
    setUploading(false)
    onClose()
  }

  const getSecurityInfo = (level) => {
    const configs = {
      red: {
        bg: 'bg-danger-soft',
        border: 'border-danger/30',
        text: 'text-red-800',
        icon: HiOutlineShieldExclamation,
        title: t('documents.security.high'),
        // TODO: needs i18n key
        description: 'Only Owner and Admin can access'
      },
      yellow: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-800',
        icon: HiOutlineExclamationTriangle,
        title: t('documents.security.medium'),
        // TODO: needs i18n key
        description: 'Owner, Admin, and Diamond can access'
      },
      green: {
        bg: 'bg-success-soft',
        border: 'border-success/30',
        text: 'text-green-800',
        icon: HiOutlineShieldCheck,
        title: t('documents.security.low'),
        // TODO: needs i18n key
        description: 'All roles can access'
      }
    }

    return configs[level] || configs.green
  }

  const securityInfo = getSecurityInfo(formData.security_level)
  const SecurityIcon = securityInfo.icon

  // TODO: needs i18n key (text/voice variants)
  const modalTitle =
    documentType === 'document'
      ? `${t('documents.upload')} ${getEntity('document').singular}`
      : documentType === 'text'
        ? 'Send text message'
        : 'Send voice message'

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      title={modalTitle}
    >
      <Modal.Body className="space-y-6">
        {/* Type-tinted subtitle row (replaces the old custom header's icon chip) */}
        <div className="flex items-center gap-3 -mt-1">
          <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
            documentType === 'document' ? 'bg-brand-soft text-brand' :
            documentType === 'text'     ? 'bg-success-soft text-success' :
                                          'bg-info-soft text-info'
          }`}>
            {documentType === 'document' && <HiOutlineArrowUpTray className="w-5 h-5" />}
            {documentType === 'text' && <HiOutlineChatBubbleLeft className="w-5 h-5" />}
            {documentType === 'voice' && <HiOutlineSpeakerWave className="w-5 h-5" />}
          </span>
          <p className="text-sm text-ink-muted">
            {documentType === 'document'
              // TODO: needs i18n key
              ? `Upload and organize your ${getEntity('document').plural.toLowerCase()}`
              : documentType === 'text'
                // TODO: needs i18n key
                ? 'Send a text message'
                // TODO: needs i18n key
                : 'Send a voice message'}
          </p>
        </div>

                {/* Block-policy banner — appears when the user can't perform the
                    selected action right now (free trial expired, drive disconnected,
                    subscription blocked, etc). Click acts as CTA. */}
                <BlockedBanner action={uploadAction} />

                {/* Document Type Selector */}
                <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
                  <button
                    type="button"
                    onClick={() => { setDocumentType('document'); setMessageScope('direct') }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      documentType === 'document'
                        ? 'bg-white text-brand shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <HiOutlineArrowUpTray className="w-4 h-4" />
                    {/* TODO: needs i18n key */}
                    Upload File
                  </button>
                  <button
                    type="button"
                    onClick={() => { setDocumentType('text'); setFiles([]) }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      documentType === 'text'
                        ? 'bg-white text-success shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <HiOutlineChatBubbleLeft className="w-4 h-4" />
                    {/* TODO: needs i18n key */}
                    Text Message
                  </button>
                  <button
                    type="button"
                    onClick={() => { setDocumentType('voice'); setFiles([]) }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      documentType === 'voice'
                        ? 'bg-white text-brand shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <HiOutlineSpeakerWave className="w-4 h-4" />
                    {/* TODO: needs i18n key */}
                    Voice Message
                  </button>
                </div>

                {/* === MESSAGE SCOPE (for text/voice only) === */}
                {(documentType === 'text' || documentType === 'voice') && (
                  <div className="space-y-3">
                    {/* TODO: needs i18n key */}
                    <label className="block text-sm font-medium text-gray-700">Message Scope</label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setMessageScope('direct')}
                        className={`flex-1 flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                          messageScope === 'direct'
                            ? 'border-brand/40 bg-brand-soft'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <HiOutlineUser className="w-5 h-5 text-brand" />
                        <div className="text-left">
                          <div className="text-sm font-medium text-gray-900">{t('messages.create.directLabel')}</div>
                          {/* TODO: needs i18n key */}
                          <div className="text-xs text-gray-500">To specific client, case, or session</div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setMessageScope('broadcast')}
                        className={`flex-1 flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                          messageScope === 'broadcast'
                            ? 'border-orange-300 bg-warning-soft'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <HiOutlineMegaphone className="w-5 h-5 text-warning" />
                        <div className="text-left">
                          <div className="text-sm font-medium text-gray-900">{t('messages.create.broadcastLabel')}</div>
                          {/* TODO: needs i18n key */}
                          <div className="text-xs text-gray-500">To all workspace members</div>
                        </div>
                      </button>
                    </div>

                    {messageScope === 'broadcast' && (
                      <div className="p-3 bg-warning-soft border border-warning/30 rounded-xl text-sm text-warning">
                        {/* TODO: needs i18n key */}
                        <strong>Broadcast:</strong> This message will be sent to all members in your workspace.
                      </div>
                    )}
                  </div>
                )}

                {/* === TEXT MESSAGE CONTENT === */}
                {documentType === 'text' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('messages.create.titleLabel')}</label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder={t('messages.create.titlePlaceholder')}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('messages.create.contentLabel')}</label>
                      <textarea
                        value={messageContent}
                        onChange={(e) => setMessageContent(e.target.value)}
                        placeholder={t('messages.create.contentPlaceholder')}
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand resize-none"
                      />
                    </div>
                  </div>
                )}

                {/* === VOICE FILE INPUT === */}
                {documentType === 'voice' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('messages.create.titleLabel')}</label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder={t('messages.create.titlePlaceholder')}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('messages.create.voiceFileLabel')}</label>
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={(e) => setVoiceFile(e.target.files[0] || null)}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-brand-soft file:text-brand hover:file:bg-brand-soft"
                      />
                      {voiceFile && (
                        <p className="mt-1 text-sm text-gray-500">
                          {/* TODO: needs i18n key */}
                          Selected: {voiceFile.name} ({(voiceFile.size / 1024 / 1024).toFixed(1)} MB)
                        </p>
                      )}
                      {/* TODO: needs i18n key */}
                      <p className="mt-1 text-xs text-gray-400">Max 10MB. Supported formats: WAV, MP3, M4A, MP4</p>
                    </div>
                  </div>
                )}

                {/* === FILE UPLOAD AREA (document type only) === */}
                {documentType === 'document' && <>
                {/* File Upload Area */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                    dragOver 
                      ? 'border-brand bg-brand-soft' 
                      : files.length > 0
                        ? 'border-green-300 bg-success-soft'
                        : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  {files.length > 0 ? (
                    <div className="space-y-4">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto">
                        <HiOutlineCheckCircle className="w-6 h-6 text-success" />
                      </div>
                      <div>
                        {/* TODO: needs i18n key */}
                        <p className="font-medium text-gray-900 mb-3">{files.length} file{files.length !== 1 ? 's' : ''} selected</p>
                        <div className="max-h-40 overflow-y-auto space-y-2">
                          {files.map((file, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                                <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                              </div>
                              <button
                                onClick={() => removeFile(index)}
                                className="ml-2 p-1 text-red-500 hover:text-danger hover:bg-danger-soft rounded transition-colors"
                                // TODO: needs i18n key
                                title="Remove file"
                              >
                                <HiOutlineXMark className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={addMoreFiles}
                          className="text-sm text-brand hover:text-brand-hover font-medium"
                        >
                          {/* TODO: needs i18n key */}
                          Add more files
                        </button>
                        <button
                          onClick={() => setFiles([])}
                          className="text-sm text-danger hover:text-danger/80 font-medium"
                        >
                          {/* TODO: needs i18n key */}
                          Clear all files
                        </button>
                      </div>
                      {/* Hidden input for adding more files */}
                      <input
                        type="file"
                        id="add-more-files-input"
                        onChange={handleAddMoreFiles}
                        multiple
                        className="hidden"
                      />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto">
                        <HiOutlineArrowUpTray className="w-6 h-6 text-gray-400" />
                      </div>
                      <div>
                        {/* TODO: needs i18n key */}
                        <p className="text-gray-700 font-medium">Drop your files here or click to browse</p>
                        <p className="text-sm text-gray-500 mt-1">
                          {/* TODO: needs i18n key */}
                          Supports all file types • Maximum file size: {(apiConfig.MAX_FILE_SIZE / (1024 * 1024)).toFixed(0)}MB per file
                        </p>
                      </div>
                      <input
                        type="file"
                        onChange={handleFileInputChange}
                        multiple
                        className="hidden"
                        id="file-input"
                      />
                      <label
                        htmlFor="file-input"
                        className="inline-block px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover cursor-pointer transition-colors"
                      >
                        {/* TODO: needs i18n key */}
                        Choose Files
                      </label>
                    </div>
                  )}
                </div>

                {/* Form Fields */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Security Level */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        {t('documents.detail.securityLevelLabel')} <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {['green', 'yellow', 'red'].map((level) => {
                          const config = getSecurityInfo(level)
                          const Icon = config.icon
                          const isDisabled = !allowedSecurityLevels.includes(level)
                          const role = userRole?.toLowerCase() || 'silver'
                          
                          return (
                            <label key={level} className={`relative flex flex-col items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                              isDisabled 
                                ? 'cursor-not-allowed opacity-50 border-gray-200 bg-gray-50'
                                : formData.security_level === level
                                  ? `cursor-pointer ${config.bg} ${config.border} ring-2 ring-offset-2 ring-brand`
                                  : 'cursor-pointer border-gray-200 hover:border-gray-300 hover:shadow-sm'
                            }`}
                            // TODO: needs i18n key
                            title={isDisabled ? `Your ${role?.toUpperCase()} role cannot create ${level?.toUpperCase()} security documents` : ''}
                            >
                              <input
                                type="radio"
                                name="security_level"
                                value={level}
                                checked={formData.security_level === level}
                                onChange={isDisabled ? undefined : handleInputChange}
                                disabled={isDisabled}
                                className="sr-only"
                              />
                              <Icon className={`w-6 h-6 mb-2 ${isDisabled ? 'text-gray-400' : config.text}`} />
                              <div className="text-center">
                                <p className={`text-sm font-medium ${isDisabled ? 'text-gray-400' : config.text}`}>{config.title}</p>
                                <p className={`text-xs ${isDisabled ? 'text-gray-400' : 'text-gray-500'} mt-1 leading-tight`}>{config.description}</p>
                              </div>
                              {formData.security_level === level && !isDisabled && (
                                <div className="absolute -top-2 -right-2 w-5 h-5 bg-brand rounded-full flex items-center justify-center">
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </label>
                          )
                        })}
                      </div>
                    </div>

                    {/* Client Selection */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                        <HiOutlineUser className="w-4 h-4 text-gray-500" />
                        {t('cases.create.clientLabel', { client: getEntity('client').singular })} <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="client_id"
                        value={formData.client_id}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/30 focus:border-brand disabled:bg-gray-100 disabled:text-gray-500"
                        required
                        disabled={disablePreselectedFields && preselectedClient || (preselectedGroup && preselectedGroup.clientId)}
                      >
                        <option value="">{t('cases.create.clientPlaceholder', { client: getEntity('client').singular })}</option>
                        {clients.map((client) => (
                          <option key={client.id} value={client.id}>
                            {client.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Case Selection */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                        <HiOutlineScale className="w-4 h-4 text-gray-500" />
                        {t('messages.create.caseOptional', { entity: getEntity('case').singular })}
                      </label>
                      <select
                        name="case_id"
                        value={formData.case_id}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/30 focus:border-brand disabled:bg-gray-100 disabled:text-gray-500"
                        disabled={!formData.client_id || (disablePreselectedFields && preselectedCase) || (preselectedGroup && preselectedGroup.caseId)}
                      >
                        <option value="">{t('messages.create.selectCase', { entity: getEntity('case').singular.toLowerCase() })}</option>
                        {cases.map((caseItem) => (
                          <option key={caseItem.id} value={caseItem.id}>
                            {caseItem.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Session Selection */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                        <HiOutlineCalendar className="w-4 h-4 text-gray-500" />
                        {t('messages.create.sessionOptional', { entity: getEntity('session').singular })}
                      </label>
                      <select
                        name="session_id"
                        value={formData.session_id}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/30 focus:border-brand disabled:bg-gray-100 disabled:text-gray-500"
                        disabled={!formData.case_id || (disablePreselectedFields && preselectedSession) || (preselectedGroup && preselectedGroup.sessionId)}
                      >
                        <option value="">{t('messages.create.selectSession', { entity: getEntity('session').singular.toLowerCase() })}</option>
                        {sessions.map((session) => (
                          <option key={session.id} value={session.id}>
                            {session.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Single Document Name */}
                    {uploadMode === 'single' && (
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                          <HiOutlineDocument className="w-4 h-4 text-gray-500" />
                          {/* TODO: needs i18n key (document name label) */}
                          {getEntity('document').singular} Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          // TODO: needs i18n key
                          placeholder={`Enter ${getEntity('document').singular.toLowerCase()} name (min. 8 characters)`}
                          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-brand/30 focus:border-brand ${
                            formData.title && formData.title.trim().length > 0 && formData.title.trim().length < 8
                              ? 'border-red-300'
                              : 'border-gray-200'
                          }`}
                          required
                        />
                        <p className={`mt-2 text-sm ${
                          formData.title && formData.title.trim().length > 0 && formData.title.trim().length < 8
                            ? 'text-red-500'
                            : 'text-gray-500'
                        }`}>
                          {/* TODO: needs i18n key */}
                          {formData.title.trim().length}/8 characters minimum
                        </p>
                        {files.length > 1 && formData.title.trim() && (
                          <div className="mt-2 p-3 bg-info-soft border border-info/30 rounded-lg">
                            <p className="text-sm text-blue-800 font-medium mb-1">
                              <HiOutlineInformationCircle className="w-4 h-4 inline mr-1" />
                              {/* TODO: needs i18n key */}
                              Auto-numbering preview:
                            </p>
                            <div className="text-xs text-brand space-y-0.5">
                              {files.slice(0, 3).map((file, idx) => (
                                <div key={idx}>
                                  {formData.title.trim()} - {idx + 1} <span className="text-blue-500">({file.name})</span>
                                </div>
                              ))}
                              {files.length > 3 && (
                                // TODO: needs i18n key
                                <div className="text-blue-500">...and {files.length - 3} more</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Join to Group (optional, in single mode) */}
                    {uploadMode === 'single' && (
                      <div>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.join_to_group}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              join_to_group: e.target.checked,
                              group_id: e.target.checked ? prev.group_id : ''
                            }))}
                            className="w-4 h-4 text-brand focus:ring-brand/30 rounded"
                          />
                          <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <HiOutlineFolder className="w-4 h-4 text-gray-500" />
                            {/* TODO: needs i18n key */}
                            Join to Existing Group
                          </span>
                        </label>
                        {formData.join_to_group && (
                          <div className="mt-3 ml-7">
                            <select
                              name="group_id"
                              value={formData.group_id}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/30 focus:border-brand"
                            >
                              {/* TODO: needs i18n key */}
                              <option value="">Select a group</option>
                              {groups.map((group) => (
                                <option key={group.id} value={group.id}>
                                  {group.name}
                                </option>
                              ))}
                            </select>
                            {groups.length === 0 && (
                              <p className="text-xs text-gray-500 mt-2">
                                {/* TODO: needs i18n key */}
                                No groups found. {!formData.client_id ? `Select a ${getEntity('client').singular.toLowerCase()} first.` : 'No groups exist for this selection.'}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Group Selection */}
                    {uploadMode === 'group' && (
                      <div className="space-y-4">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                          <HiOutlineFolder className="w-4 h-4 text-gray-500" />
                          {/* TODO: needs i18n key (document group label) */}
                          {getEntity('document').singular} Group
                          {preselectedGroup && preselectedGroup.groupId && (
                            <span className="ml-2 px-2 py-1 bg-info-soft text-info text-xs rounded-full">
                              {/* TODO: needs i18n key */}
                              Uploading to: {preselectedGroup.groupName}
                            </span>
                          )}
                        </label>

                        <div className="space-y-3">
                          <label className="flex items-center gap-3">
                            <input
                              type="radio"
                              name="groupOption"
                              checked={!formData.create_new_group}
                              onChange={() => setFormData(prev => ({ ...prev, create_new_group: false }))}
                              className="text-brand focus:ring-brand/30"
                              disabled={preselectedGroup && preselectedGroup.groupId}
                            />
                            {/* TODO: needs i18n key */}
                            <span className="text-sm text-gray-700">Use existing group</span>
                          </label>

                          {!formData.create_new_group && (
                            <select
                              name="group_id"
                              value={formData.group_id}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/30 focus:border-brand ml-6 disabled:bg-gray-100 disabled:text-gray-500"
                              disabled={preselectedGroup && preselectedGroup.groupId}
                            >
                              {/* TODO: needs i18n key */}
                              <option value="">Select a group</option>
                              {groups.map((group) => (
                                <option key={group.id} value={group.id}>
                                  {group.name}
                                </option>
                              ))}
                            </select>
                          )}

                          <label className="flex items-center gap-3">
                            <input
                              type="radio"
                              name="groupOption"
                              checked={formData.create_new_group}
                              onChange={() => setFormData(prev => ({ ...prev, create_new_group: true }))}
                              className="text-brand focus:ring-brand/30"
                              disabled={preselectedGroup && preselectedGroup.groupId}
                            />
                            {/* TODO: needs i18n key */}
                            <span className="text-sm text-gray-700">Create new group</span>
                          </label>

                          {formData.create_new_group && (
                            <div className="space-y-3 ml-6">
                              <input
                                type="text"
                                name="new_group_name"
                                value={formData.new_group_name}
                                onChange={handleInputChange}
                                // TODO: needs i18n key
                                placeholder="Enter group name"
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/30 focus:border-brand"
                              />
                              <textarea
                                name="new_group_description"
                                value={formData.new_group_description}
                                onChange={handleInputChange}
                                // TODO: needs i18n key
                                placeholder="Enter group description (optional)"
                                rows={3}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/30 focus:border-brand resize-none"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                </>}

                {/* Security Level for text/voice messages */}
                {(documentType === 'text' || documentType === 'voice') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('documents.detail.securityLevelLabel')} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-3">
                      {[...allowedSecurityLevels].sort((a, b) => {
                        const order = { green: 0, yellow: 1, red: 2 }
                        return (order[a] ?? 9) - (order[b] ?? 9)
                      }).map(level => {
                        const info = getSecurityInfo(level)
                        const SecurityIcon = info.icon
                        return (
                          <label
                            key={level}
                            className={`flex-1 flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                              formData.security_level === level
                                ? `${info.border} ${info.bg}`
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="msg_security_level"
                              value={level}
                              checked={formData.security_level === level}
                              onChange={(e) => setFormData(prev => ({ ...prev, security_level: e.target.value }))}
                              className="sr-only"
                            />
                            <SecurityIcon className={`w-4 h-4 ${info.text}`} />
                            <span className={`text-sm font-medium ${formData.security_level === level ? info.text : 'text-gray-700'}`}>
                              {info.title}
                            </span>
                          </label>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Client/Case/Session selectors for message types (direct scope) */}
                {(documentType === 'text' || documentType === 'voice') && messageScope === 'direct' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{getEntity('client').singular}</label>
                      <select
                        name="client_id"
                        value={formData.client_id}
                        onChange={handleInputChange}
                        disabled={disablePreselectedFields && preselectedClient}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand"
                      >
                        <option value="">{t('messages.create.selectClient', { entity: getEntity('client').singular.toLowerCase() })}</option>
                        {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{getEntity('case').singular}</label>
                      <select
                        name="case_id"
                        value={formData.case_id}
                        onChange={handleInputChange}
                        disabled={(disablePreselectedFields && preselectedCase) || !formData.client_id}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand"
                      >
                        <option value="">{t('messages.create.selectCase', { entity: getEntity('case').singular.toLowerCase() })}</option>
                        {cases.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{getEntity('session').singular}</label>
                      <select
                        name="session_id"
                        value={formData.session_id}
                        onChange={handleInputChange}
                        disabled={(disablePreselectedFields && preselectedSession) || !formData.case_id}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand"
                      >
                        <option value="">{t('messages.create.selectSession', { entity: getEntity('session').singular.toLowerCase() })}</option>
                        {sessions.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
                      </select>
                    </div>
                  </div>
                )}

                {/* Security Info */}
                <div className={`p-4 rounded-xl ${securityInfo.bg} ${securityInfo.border} border`}>
                  <div className="flex items-start gap-3">
                    <SecurityIcon className={`w-5 h-5 ${securityInfo.text} mt-0.5`} />
                    <div className="flex-1">
                      <h4 className={`text-sm font-medium ${securityInfo.text} mb-2`}>
                        {securityInfo.title} {getEntity('document').plural}
                      </h4>
                      <p className="text-sm text-ink-secondary">
                        {/* TODO: needs i18n key */}
                        {securityInfo.description}. Make sure you select the appropriate security level based on the sensitivity of your {getEntity('document').plural.toLowerCase()}.
                      </p>
                    </div>
                  </div>
                </div>
      </Modal.Body>

      <Modal.Footer className="!items-stretch !flex-col !gap-3 !justify-start">
        {/* Upload Progress Bar */}
        {uploading && documentType === 'document' && (
          <div className="space-y-2 w-full">
            <div className="flex items-center justify-between text-sm">
              {/* TODO: needs i18n key */}
              <span className="text-ink-secondary font-medium">Uploading files…</span>
              <span className="text-brand font-bold text-base">{uploadProgress}%</span>
            </div>
            <div className="relative w-full bg-muted rounded-full h-3 overflow-hidden">
              <div
                className="absolute inset-y-0 start-0 bg-gradient-to-r from-brand to-brand-hover rounded-full transition-all duration-300 ease-out"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex items-center justify-between gap-2 w-full">
          <Button variant="ghost" onClick={handleClose} disabled={uploading}>
            {t('common.cancel')}
          </Button>
          <BlockedAction action={uploadAction} mode="inline">
            <Button
              onClick={handleUpload}
              loading={uploading}
              disabled={
                uploading ||
                (documentType === 'document' && files.length === 0) ||
                (documentType === 'text' && !messageContent.trim()) ||
                (documentType === 'voice' && !voiceFile)
              }
              leadingIcon={
                documentType === 'document' ? HiOutlineArrowUpTray :
                documentType === 'text'     ? HiOutlineChatBubbleLeft :
                                              HiOutlineSpeakerWave
              }
              className="min-w-[180px]"
            >
              {uploading
                // TODO: needs i18n key (Uploading…/Sending…)
                ? (documentType === 'document' ? 'Uploading…' : 'Sending…')
                : documentType === 'document'
                  // TODO: needs i18n key (parametrized upload button)
                  ? `${t('documents.upload')} ${files.length > 0 ? `${files.length} ${files.length !== 1 ? getEntity('document').plural : getEntity('document').singular}` : getEntity('document').plural}`
                  : documentType === 'text'
                    ? t('messages.create.submit')
                    // TODO: needs i18n key (Send voice)
                    : 'Send voice'}
            </Button>
          </BlockedAction>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default DocumentUploadModal
